import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TournamentLogo } from './tournament-logo';
import { TeamLogo } from './team-logo';
import { CalendarDays, DollarSign, MapPin, Users, UserPlus, ExternalLink } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { isTournamentLive } from '@shared/timeUtils';
import type { Team } from '@shared/schema';

function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value?.trim()) return null;

  const datePart = value.slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);

  const parsed = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12)
    : new Date(value);

  return isValid(parsed) ? parsed : null;
}

function formatCalendarDate(
  value: string | null | undefined,
  formatString: string,
  fallback = 'TBD'
): string {
  const parsed = parseCalendarDate(value);
  if (!parsed) return fallback;
  return format(parsed, formatString);
}

interface TournamentDetailModalProps {
  tournament: { id: string };
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TournamentDetails {
  tournament: {
    id: string;
    name: string;
    customName?: string | null;
    startDate: string;
    endDate: string;
    registrationFee: number | null;
    applicationFee: number | null;
    registrationDeadline: string | null;
    maxTeams: number | null;
    isRegistrationOpen: boolean;
    diamondDetails?: unknown;
    selectedDiamondIds?: string[] | null;
    logoUrl?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
  };
  organization: {
    id: string;
    name: string;
    slug: string;
    logoUrl?: string | null;
  } | null;
  teams: Team[];
  divisions?: Array<{
    id: string;
    name: string;
    registrationFee: number | null;
  }>;
  registrationStats: {
    paidCount: number;
    maxTeams: number | null;
    isFull: boolean;
    availableSpots: number | null;
  };
  venues?: Array<{
    name: string;
    subVenue?: string | null;
  }>;
}

export function TournamentDetailModal({
  tournament,
  isOpen,
  onOpenChange,
}: TournamentDetailModalProps) {
  const [, setLocation] = useLocation();

  const {
    data: details,
    isLoading,
    error,
  } = useQuery<TournamentDetails>({
    queryKey: ['/api/public/tournaments', tournament.id, 'details'],
    queryFn: async () => {
      const response = await fetch(`/api/public/tournaments/${tournament.id}/details`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch tournament details');
      return response.json();
    },
    enabled: isOpen && !!tournament.id,
  });

  const formatCurrency = (cents: number | null | undefined) => {
    if (!cents) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const showRegister = Boolean(
    details?.tournament.isRegistrationOpen && !details.registrationStats.isFull
  );
  const showFullTournament = details
    ? isTournamentLive(details.tournament.startDate, details.tournament.endDate)
    : false;

  // Determine title and description based on state
  const dialogTitle = isLoading
    ? 'Loading Tournament Details'
    : error
      ? 'Error Loading Tournament'
      : details
        ? details.tournament.customName || details.tournament.name
        : 'Tournament Details';

  const dialogDescription = isLoading
    ? 'Please wait while we fetch the tournament information.'
    : error
      ? error instanceof Error
        ? error.message
        : 'Failed to load tournament details'
      : details
        ? details.organization?.name || 'Tournament details'
        : 'View tournament information and registration details.';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {details && (
          <div className="flex items-center gap-4 mb-4 -mt-2">
            <TournamentLogo
              logoUrl={details.tournament.logoUrl}
              tournamentName={details.tournament.customName || details.tournament.name}
              size="lg"
              fallbackColor={details.tournament.primaryColor || undefined}
            />
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-foreground">
                {details.tournament.customName || details.tournament.name}
              </h2>
              {details.organization && (
                <p className="text-base mt-1 text-muted-foreground">{details.organization.name}</p>
              )}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-destructive mb-4">Failed to load tournament details</p>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : details ? (
          <>
            <div className="space-y-6 mt-4">
              {/* Dates Section */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <CalendarDays className="w-5 h-5" />
                  Tournament Dates
                </h3>
                <div className="pl-7 space-y-1">
                  <p className="text-foreground">
                    <span className="font-medium">Start:</span>{' '}
                    {formatCalendarDate(details.tournament.startDate, 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-foreground">
                    <span className="font-medium">End:</span>{' '}
                    {formatCalendarDate(details.tournament.endDate, 'EEEE, MMMM d, yyyy')}
                  </p>
                  {details.tournament.registrationDeadline && (
                    <p className="text-muted-foreground text-sm">
                      <span className="font-medium">Registration Deadline:</span>{' '}
                      {formatCalendarDate(details.tournament.registrationDeadline, 'MMMM d, yyyy')}
                    </p>
                  )}
                </div>
              </div>

              {/* Pricing Section */}
              {(details.divisions && details.divisions.length > 0) ||
              details.tournament.registrationFee ? (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Registration Cost
                  </h3>
                  <div className="pl-7 space-y-1">
                    {details.divisions && details.divisions.length > 0 ? (
                      // Show all divisions with their registration fees (if set)
                      details.divisions.map(division => (
                        <p key={division.id} className="text-foreground">
                          <span className="font-medium">{division.name}:</span>{' '}
                          {division.registrationFee
                            ? formatCurrency(division.registrationFee)
                            : 'TBD'}
                        </p>
                      ))
                    ) : details.tournament.registrationFee ? (
                      // Fallback to tournament-level registration fee if no divisions
                      <p className="text-foreground">
                        <span className="font-medium">Registration Fee:</span>{' '}
                        {formatCurrency(details.tournament.registrationFee)}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {/* Teams Section */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Registered Teams
                </h3>
                <div className="pl-7">
                  <p className="text-foreground mb-3">
                    <span className="font-medium">{details.teams.length}</span> team
                    {details.teams.length !== 1 ? 's' : ''} registered
                    {details.registrationStats.maxTeams && (
                      <span className="text-muted-foreground">
                        {' '}
                        (
                        {details.registrationStats.availableSpots !== null
                          ? `${details.registrationStats.availableSpots} spots available`
                          : 'No limit'}
                        )
                      </span>
                    )}
                  </p>
                  {details.teams.length > 0 ? (
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                      {details.teams.map(team => (
                        <div
                          key={team.id}
                          className="flex flex-col items-center gap-1"
                          title={team.name}
                        >
                          <TeamLogo team={team} size="md" />
                          <p className="text-xs text-muted-foreground text-center line-clamp-2">
                            {team.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No teams registered yet</p>
                  )}
                </div>
              </div>

              {/* Location Section */}
              {details.venues && details.venues.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Location
                  </h3>
                  <div className="pl-7 space-y-1">
                    {details.venues.map((venue, idx) => (
                      <p key={idx} className="text-foreground">
                        {venue.name}
                        {venue.subVenue && (
                          <span className="text-muted-foreground"> - {venue.subVenue}</span>
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {(showRegister || showFullTournament) && (
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
                  {showRegister && (
                    <Button
                      className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                      onClick={() => {
                        onOpenChange(false);
                        setLocation(`/tournaments/${tournament.id}/register`);
                      }}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Register Team
                    </Button>
                  )}
                  {showFullTournament && (
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        onOpenChange(false);
                        setLocation(`/tournament/${tournament.id}`);
                      }}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Full Tournament
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
